// ---- The pan cooking minigame (plan 25) ---------------------------------------
// reel.ts's cooking twin: FLIP/STIR/SEASON/TOSS prompts fall down four W/A/S/D
// lanes toward a hit line; press the matching key as each crosses. Hits keep
// the dish under control, misses raise the BURN meter — the same pure lane
// judge the server runs (sim/cooking.ts applyPanResult over sim/lane.ts)
// mirrors here for DISPLAY; the server's copy is the verdict, arriving as the
// ordinary cookResult resolution.
//
// Judging rules (must match the server's exactly-next contract):
//   - a press within ±hitWindow of the NEXT unjudged prompt, with the right
//     lane → HIT (judged immediately);
//   - a wrong-lane or badly-timed press is CONSUMED but judges nothing;
//   - a prompt expires unpressed past +hitWindow → MISS.
// So prompt `i` is judged exactly once, in order — `ev.onInput(i, hit)` streams
// straight into the `panInput` wire intent.
//
// Import discipline: sim/cooking.ts + types.ts only — NO game.ts/net.ts/state
// imports, so the studio Skill-Sim driver mounts the LITERAL same module.
import {
  startPanJudge, applyPanResult,
  type PanPattern, type PanTuning, type PanJudge,
} from "../sim/cooking.ts";
import type { Minigame, ReelGameEvents } from "./types.ts";

const LANE_KEYS = ["w", "a", "s", "d"] as const;
const LANE_WORDS = ["FLIP", "STIR", "SEASON", "TOSS"] as const;
const LANE_GLYPHS = ["🔄", "🥄", "🧂", "🤚"] as const;
const ALIAS: Record<string, number> = {
  w: 0, a: 1, s: 2, d: 3,
  ArrowUp: 0, ArrowLeft: 1, ArrowDown: 2, ArrowRight: 3,
};

const LANE_H = 240;      // px of visible approach above the hit line
const PX_PER_SEC = 150;  // prompt fall speed
const HIT_Y = LANE_H - 40; // the hit line's y inside a lane (room for key + word below)

export function makePanMinigame(cfg: {
  pattern: PanPattern;
  /** ± seconds a press counts (server-derived: tunable × box panWindowMul). */
  hitWindow: number;
  /** Burn arithmetic for the DISPLAY judge (pass COOKING_TUNING / a sandbox copy). */
  tun: PanTuning;
  ev: ReelGameEvents;
  /** Header pill above the lanes; pass "" to omit it. */
  title?: string;
}): Minigame {
  const { pattern, hitWindow, tun, ev } = cfg;
  const title = cfg.title ?? "🍳 COOK — match the prompts!";
  let root: HTMLElement | null = null;
  let burnFill: HTMLElement | null = null;
  let progressEl: HTMLElement | null = null;
  let promptEls: HTMLElement[] = [];
  let laneEls: HTMLElement[] = [];
  let judge: PanJudge = startPanJudge(pattern, hitWindow);
  let elapsed = 0;
  let done = false;

  const flashLane = (lane: number, ok: boolean) => {
    const el = laneEls[lane];
    if (!el) return;
    el.style.background = ok ? "rgba(120,220,140,0.28)" : "rgba(230,90,80,0.28)";
    setTimeout(() => { el.style.background = "rgba(26,14,8,0.6)"; }, 120);
  };

  const judgeNext = (hit: boolean) => {
    const i = judge.idx;
    const evs = applyPanResult(judge, hit, tun);
    const el = promptEls[i];
    if (el) {
      el.style.opacity = "0.25";
      el.style.filter = hit ? "none" : "grayscale(1) brightness(0.7)";
    }
    ev.onInput(i, hit);
    if (evs.length) done = true; // display-side resolution — input stops mattering
  };

  return {
    mount() {
      if (root) return;
      root = document.createElement("div");
      root.id = "minigame";
      root.style.cssText =
        "position:fixed;left:50%;bottom:120px;transform:translateX(-50%);z-index:60;" +
        "display:flex;flex-direction:column;align-items:center;gap:6px;pointer-events:none;" +
        "font-family:inherit;user-select:none;";

      const lanes = document.createElement("div");
      lanes.style.cssText = "display:flex;gap:8px;";
      laneEls = [];
      promptEls = [];
      for (let lane = 0; lane < 4; lane++) {
        const laneEl = document.createElement("div");
        laneEl.style.cssText =
          `position:relative;width:58px;height:${LANE_H}px;overflow:hidden;border-radius:8px;` +
          "background:rgba(26,14,8,0.6);border:1px solid rgba(255,196,120,0.22);transition:background 80ms;";
        // the hit line + key + verb labels
        const line = document.createElement("div");
        line.style.cssText =
          `position:absolute;left:0;right:0;top:${HIT_Y}px;height:2px;background:rgba(255,214,140,0.65);`;
        const label = document.createElement("div");
        label.textContent = LANE_KEYS[lane]!.toUpperCase();
        label.style.cssText =
          `position:absolute;left:0;right:0;top:${HIT_Y + 4}px;text-align:center;` +
          "font-size:13px;font-weight:700;color:rgba(255,255,255,0.7);";
        const word = document.createElement("div");
        word.textContent = LANE_WORDS[lane]!;
        word.style.cssText =
          `position:absolute;left:0;right:0;top:${HIT_Y + 21}px;text-align:center;` +
          "font-size:9px;font-weight:700;letter-spacing:0.4px;color:rgba(255,205,130,0.75);";
        laneEl.append(line, label, word);
        lanes.appendChild(laneEl);
        laneEls.push(laneEl);
      }
      // one element per prompt, parked in its lane; tick() moves them
      for (let i = 0; i < pattern.arrows.length; i++) {
        const a = pattern.arrows[i]!;
        const el = document.createElement("div");
        el.textContent = LANE_GLYPHS[a.dir]!;
        el.style.cssText =
          "position:absolute;left:0;right:0;text-align:center;font-size:24px;line-height:1;" +
          "text-shadow:0 1px 3px rgba(0,0,0,0.7);will-change:transform;transform:translateY(-40px);";
        laneEls[a.dir]!.appendChild(el);
        promptEls.push(el);
      }

      const burnRow = document.createElement("div");
      burnRow.style.cssText = "display:flex;align-items:center;gap:6px;";
      const burnLabel = document.createElement("div");
      burnLabel.textContent = "BURN";
      burnLabel.style.cssText =
        "font-size:10px;font-weight:800;letter-spacing:1px;color:#e6a05a;text-shadow:0 1px 2px rgba(0,0,0,0.8);";
      const burn = document.createElement("div");
      burn.style.cssText =
        "width:200px;height:8px;border-radius:4px;background:rgba(26,14,8,0.65);" +
        "border:1px solid rgba(255,196,120,0.22);overflow:hidden;";
      burnFill = document.createElement("div");
      burnFill.style.cssText =
        "height:100%;width:0%;background:linear-gradient(90deg,#e6b45a,#e66a5a,#c93a2e);transition:width 90ms;";
      burn.appendChild(burnFill);
      burnRow.append(burnLabel, burn);

      progressEl = document.createElement("div");
      progressEl.style.cssText = "font-size:12px;color:rgba(255,255,255,0.8);text-shadow:0 1px 2px rgba(0,0,0,0.8);";
      progressEl.textContent = `cook it! 0 / ${pattern.arrows.length}`;

      if (title) {
        const titleEl = document.createElement("div");
        titleEl.textContent = title;
        titleEl.style.cssText =
          "font:bold 15px ui-monospace,monospace;color:#ffd86a;white-space:nowrap;" +
          "background:rgba(0,0,0,0.67);padding:8px 16px;border-radius:20px;box-shadow:0 2px 10px #0006;";
        root.append(titleEl, lanes, burnRow, progressEl);
      } else {
        root.append(lanes, burnRow, progressEl);
      }
      document.body.appendChild(root);
      judge = startPanJudge(pattern, hitWindow);
      elapsed = 0;
      done = false;
    },

    tick(dt: number) {
      if (!root) return;
      elapsed += dt;
      // expire the next prompt(s) past the window → misses
      while (!done && judge.idx < pattern.arrows.length && elapsed > pattern.arrows[judge.idx]!.t + hitWindow) {
        flashLane(pattern.arrows[judge.idx]!.dir, false);
        judgeNext(false);
      }
      // judgeNext → ev.onInput can UNMOUNT us reentrantly: the studio driver
      // resolves its own judge synchronously, so a burn-out via an expired miss
      // tears this overlay down mid-tick (the game path unmounts later, off the
      // server echo). Bail before touching the now-cleared DOM.
      if (!root) return;
      // scroll every unjudged prompt toward the hit line
      for (let i = judge.idx; i < pattern.arrows.length; i++) {
        const a = pattern.arrows[i]!;
        const y = HIT_Y - (a.t - elapsed) * PX_PER_SEC;
        promptEls[i]!.style.transform = `translateY(${Math.round(y - 24)}px)`;
      }
      if (burnFill) burnFill.style.width = `${Math.min(100, judge.tension * 100)}%`;
      if (progressEl && !done) progressEl.textContent = `cook it! ${judge.idx} / ${pattern.arrows.length}`;
      if (progressEl && done) progressEl.textContent = judge.tension >= 1 ? "it's burning…" : "plating up…";
    },

    key(e: KeyboardEvent): boolean {
      if (!root || done) return false;
      const dir = ALIAS[e.key.length === 1 ? e.key.toLowerCase() : e.key];
      if (dir === undefined) return false; // not ours (Esc, Space, …)
      const i = judge.idx;
      const a = pattern.arrows[i];
      if (a && a.dir === dir && Math.abs(elapsed - a.t) <= hitWindow) {
        flashLane(dir, true);
        judgeNext(true);
      } else {
        flashLane(dir, false); // wrong lane / bad timing: consumed, judges nothing
      }
      return true;
    },

    unmount() {
      root?.remove();
      root = null;
      burnFill = null;
      progressEl = null;
      promptEls = [];
      laneEls = [];
    },
  };
}
